import { RegularMetricId } from '../../../api/types-graphql';
import {
  DashboardConfig,
  DashboardPageOptions,
} from '../../../common/components/dashboards/types';
import { OverTimeUnsegmentedVizViews } from '../../../common/components/data-viz-containers/over-time-unsegmented/types';
import { StoryboardComponentIds } from '../../../common/components/storyboard-components/common/types';
import { TimeSliderTypes } from '../../../common/components/timeslider/types';
import { Languages } from '../../../constants';
import { EmployeeDataFields, TimeSelectionType } from '../../../types';
import {
  getAgeFactbox,
  getApplicationsByGenderChart,
  getApplicationsBySourceChart,
  getAttritionChart,
  getDemographicBySegmentChart,
  getFemaleManagersByDepartmentChart,
  getFemaleManagersChart,
  getHeadcountByEmploymentTypeChart,
  getHeadcountByYearChart,
  getHeadcountFactboxSegmentWithGrid,
  getHeadcountFactboxWithGrid,
  getHeadcountOverTimeChart,
  getIndividualsByDeptAndAgeChart,
  getIndividualsCountChart,
  getIndividualsFactbox,
  getJoinersLeaversBySegmentChart,
  getJoinersLeaversFactbox,
  getJoinersLeaversOverTimeChart,
  getJoinersOverTimeChart,
  getLeaversByGenderChart,
  getManagementPromotionChart,
  getMultiMetricLineChart,
  getNewJoinersManagersByGenderChart,
  getNewManagersByGenderChart,
  getParentalLeaveFactbox,
  getTenureFactbox,
} from './alex-chart-helpers';

const pageOptions: DashboardPageOptions = {
  displayOptions: {
    enabled: true,
  },
  timeOptions: {
    timeSliderEnabled: false,
  },
  filterTray: {
    enabled: true,
  },
  dataView: {
    enabled: false,
  },
  segmentationLevel1: {
    enabled: true,
  },
  segmentationLevel2: {
    enabled: false,
  },
  benchmark: {
    enabled: false,
  },
  forecast: {
    enabled: false,
  },
};

const pastelColors = [
  '#FF9999', // Brighter Pink
  '#99CC99', // Brighter Green
  '#99CCFF', // Brighter Blue
  '#FFFF99', // Brighter Yellow
  '#FFCC99', // Brighter Orange
  '#CC99FF', // Brighter Purple
  '#99E6CC', // Brighter Mint
  '#FF99CC', // Brighter Rose
  '#CC99FF', // Brighter Lavender
  '#E6CC99', // Brighter Beige
];
const colors = pastelColors;

export const AlexDashboardChartConfig: DashboardConfig = {
  id: 'themis',
  title: {
    [Languages.EN]: 'Themis Project',
    [Languages.JA]: 'テミスプロジェクト',
  },
  enabled: true,
  pages: [
    {
      pageId: '1',
      dashboardId: 'themis',
      pageNumber: 1,
      enabled: true,
      title: {
        [Languages.EN]: 'DEMAND',
        [Languages.JA]: '需要',
      },
      pageOptions,
      configType: 'componentConfigs',
      components: ({ storyboardDateUtils }) => [
        getAttritionChart(storyboardDateUtils),
        getMultiMetricLineChart(storyboardDateUtils),
        getJoinersOverTimeChart(storyboardDateUtils),
        getIndividualsCountChart(storyboardDateUtils),
        getNewJoinersManagersByGenderChart(storyboardDateUtils),
        getLeaversByGenderChart(storyboardDateUtils),
      ],
      gridCols: 3,
      gridRows: 2,
    },
    {
      pageId: '2',
      dashboardId: 'themis',
      pageNumber: 2,
      enabled: true,
      title: {
        [Languages.EN]: 'SUPPLY',
        [Languages.JA]: '供給',
      },
      pageOptions,
      configType: 'componentConfigs',
      components: ({ storyboardDateUtils }) => [
        getFemaleManagersChart(storyboardDateUtils),
        getNewManagersByGenderChart(storyboardDateUtils),
        getFemaleManagersByDepartmentChart(storyboardDateUtils),
        getManagementPromotionChart(storyboardDateUtils),
        getApplicationsByGenderChart(storyboardDateUtils),
        getApplicationsBySourceChart(storyboardDateUtils),
      ],
      gridCols: 3,
      gridRows: 2,
    },
    {
      pageId: '3',
      dashboardId: 'themis',
      pageNumber: 3,
      enabled: true,
      title: {
        [Languages.EN]: 'OFFER',
        [Languages.JA]: 'オファー',
      },
      pageOptions,
      configType: 'componentConfigs',
      components: ({ storyboardDateUtils }) => [
        getParentalLeaveFactbox(storyboardDateUtils),
        getIndividualsByDeptAndAgeChart(storyboardDateUtils),
      ],
      gridCols: 4,
      gridRows: 1,
    },
    {
      pageId: '4',
      dashboardId: 'themis',
      pageNumber: 4,
      enabled: true,
      title: {
        [Languages.EN]: 'Themis 4',
        [Languages.JA]: 'テミス4',
      },
      pageOptions,
      configType: 'componentConfigs',
      components: ({ storyboardDateUtils }) => [
        getIndividualsFactbox(storyboardDateUtils),
        getJoinersLeaversFactbox(storyboardDateUtils),
        getIndividualsFactbox(storyboardDateUtils),
        getJoinersLeaversFactbox(storyboardDateUtils),
        getIndividualsByDeptAndAgeChart(storyboardDateUtils),
        getJoinersLeaversOverTimeChart(storyboardDateUtils),
      ],
      gridRows: 3,
      gridCols: 4,
    },
    {
      pageId: '5',
      dashboardId: 'themis',
      pageNumber: 5,
      enabled: true,
      title: {
        [Languages.EN]: 'Joiners & Leavers',
        [Languages.JA]: '入社者と退職者',
      },
      pageOptions,
      gridCols: 4,
      configType: 'componentConfigs',
      components: ({ storyboardDateUtils }) => [
        getJoinersLeaversOverTimeChart(storyboardDateUtils),
        getJoinersLeaversBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.ORGANIZATION_LEVEL_1,
          'joiners-and-leavers-count-by-org',
          {
            [Languages.EN]: 'Organization',
            [Languages.JA]: '組織',
          },
          1
        ),
        getJoinersLeaversBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.LOCATION_LEVEL_1,
          'joiners-and-leavers-count-by-location',
          {
            [Languages.EN]: 'Location',
            [Languages.JA]: '場所',
          },
          2
        ),
        getJoinersLeaversBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.TENURE_GROUP,
          'joiners-and-leavers-count-by-tenure',
          {
            [Languages.EN]: 'Tenure',
            [Languages.JA]: '在職期間',
          },
          1
        ),
        getJoinersLeaversBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.GENDER,
          'joiners-and-leavers-count-by-gender',
          {
            [Languages.EN]: 'Gender',
            [Languages.JA]: '性別',
          },
          1
        ),
        getJoinersLeaversBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.JOB_GRADE_LEVEL_1,
          'joiners-and-leavers-count-by-job-grade',
          {
            [Languages.EN]: 'Job Grade',
            [Languages.JA]: '職級',
          },
          1
        ),
        getJoinersLeaversBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_1,
          'joiners-and-leavers-count-by-employment-type',
          {
            [Languages.EN]: 'Employment Type',
            [Languages.JA]: '雇用形態',
          },
          1
        ),
        getJoinersLeaversBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.AGE_GROUP,
          'joiners-and-leavers-count-by-age-group',
          {
            [Languages.EN]: 'Age Group',
            [Languages.JA]: '年齢層',
          },
          1
        ),
      ],
    },
    {
      pageId: '6',
      dashboardId: 'themis',
      pageNumber: 6,
      enabled: true,
      title: {
        [Languages.EN]: 'People Snapshot: Headcount',
        [Languages.JA]: '供給',
      },
      pageOptions,
      gridCols: 3,
      configType: 'componentConfigs',
      components: ({ storyboardDateUtils }) => [
        getHeadcountOverTimeChart(storyboardDateUtils),
        getHeadcountByEmploymentTypeChart(storyboardDateUtils),
        getIndividualsFactbox(storyboardDateUtils),
        getHeadcountByYearChart(storyboardDateUtils),
      ],
    },
    {
      pageId: '7',
      dashboardId: 'themis',
      pageNumber: 7,
      enabled: true,
      title: {
        [Languages.EN]: 'People Snapshot: Demographics(Headcount)',
        [Languages.JA]: '人口統計(従業員数)',
      },
      pageOptions,
      gridCols: 6,
      gridRows: 4,
      configType: 'componentConfigs',
      components: ({ storyboardDateUtils }) => [
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.ORGANIZATION_LEVEL_1,
          'people-snapshot-demographic-by-organization',
          {
            [Languages.EN]: 'Organization',
            [Languages.JA]: '組織',
          },
          1,
          3,
          1,
          3
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.LOCATION_LEVEL_1,
          'people-snapshot-demographic-by-location',
          {
            [Languages.EN]: 'Location',
            [Languages.JA]: '場所',
          },
          3,
          6,
          1,
          3
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.TENURE_GROUP,
          'people-snapshot-demographic-by-tenure',
          {
            [Languages.EN]: 'Tenure',
            [Languages.JA]: '在職期間',
          },
          1,
          2,
          3,
          5
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.GENDER,
          'people-snapshot-demographic-by-gender',
          {
            [Languages.EN]: 'Gender',
            [Languages.JA]: '性別',
          },
          2,
          3,
          3,
          5,
          true
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.JOB_GRADE_LEVEL_1,
          'people-snapshot-demographic-by-job-grade',
          {
            [Languages.EN]: 'Job Grade',
            [Languages.JA]: '職級',
          },
          3,
          4,
          3,
          5
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_1,
          'people-snapshot-demographic-by-employment-type',
          {
            [Languages.EN]: 'Employment Type',
            [Languages.JA]: '雇用形態',
          },
          4,
          5,
          3,
          5
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.AGE_GROUP,
          'people-snapshot-demographic-by-age-group',
          {
            [Languages.EN]: 'Age Group',
            [Languages.JA]: '年齢層',
          },
          5,
          6,
          3,
          5
        ),
        getHeadcountFactboxWithGrid(storyboardDateUtils, 6, 7, 1, 2),
        getTenureFactbox(storyboardDateUtils, 6, 7, 2, 3),
        getHeadcountFactboxWithGrid(storyboardDateUtils, 6, 7, 3, 4),
        getAgeFactbox(storyboardDateUtils, 6, 7, 4, 5),
      ],
    },
    {
      pageId: '8',
      dashboardId: 'themis',
      pageNumber: 8,
      enabled: true,
      title: {
        [Languages.EN]: 'People Snapshot with Time slider',
        [Languages.JA]: '時間スライダー付きの人口統計',
      },
      pageOptions: {
        ...pageOptions,
        timeOptions: {
          timeSliderEnabled: true,
          timeSliderType: TimeSliderTypes.RANGE,
        },
      },
      gridCols: 6,
      gridRows: 4,
      configType: 'componentConfigs',
      components: ({ storyboardDateUtils }) => [
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.ORGANIZATION_LEVEL_1,
          'people-snapshot-demographic-by-organization',
          {
            [Languages.EN]: 'Organization',
            [Languages.JA]: '組織',
          },
          1,
          3,
          1,
          3
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.LOCATION_LEVEL_1,
          'people-snapshot-demographic-by-location',
          {
            [Languages.EN]: 'Location',
            [Languages.JA]: '場所',
          },
          3,
          6,
          1,
          3
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.TENURE_GROUP,
          'people-snapshot-demographic-by-tenure',
          {
            [Languages.EN]: 'Tenure',
            [Languages.JA]: '在職期間',
          },
          1,
          2,
          3,
          5
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.GENDER,
          'people-snapshot-demographic-by-gender',
          {
            [Languages.EN]: 'Gender',
            [Languages.JA]: '性別',
          },
          2,
          3,
          3,
          5,
          true
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.JOB_GRADE_LEVEL_1,
          'people-snapshot-demographic-by-job-grade',
          {
            [Languages.EN]: 'Job Grade',
            [Languages.JA]: '職級',
          },
          3,
          4,
          3,
          5
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.EMPLOYMENT_TYPE_LEVEL_1,
          'people-snapshot-demographic-by-employment-type',
          {
            [Languages.EN]: 'Employment Type',
            [Languages.JA]: '雇用形態',
          },
          4,
          5,
          3,
          5
        ),
        getDemographicBySegmentChart(
          storyboardDateUtils,
          EmployeeDataFields.AGE_GROUP,
          'people-snapshot-demographic-by-age-group',
          {
            [Languages.EN]: 'Age Group',
            [Languages.JA]: '年齢層',
          },
          5,
          6,
          3,
          5
        ),
        getHeadcountFactboxWithGrid(storyboardDateUtils, 6, 7, 1, 2),
        getTenureFactbox(storyboardDateUtils, 6, 7, 2, 3),
        getHeadcountFactboxSegmentWithGrid(storyboardDateUtils, 6, 7, 3, 4),
        getAgeFactbox(storyboardDateUtils, 6, 7, 4, 5),
      ],
    },
    {
      pageId: '9',
      dashboardId: 'themis',
      pageNumber: 9,
      enabled: true,
      title: {
        [Languages.EN]: 'G3 Page',
        [Languages.JA]: 'G3 ページ',
      },
      pageOptions: {
        dataView: {
          enabled: true,
        },
        timeOptions: { timeSliderEnabled: false },
      },
      gridRows: 1,
      gridCols: 3,
      configType: 'componentConfigs',
      components: ({
        storyboardDateUtils: {
          twelveMonthsAgoFromNowEndOfMonth,
          nowEndOfMonth,
        },
      }) => [
        {
          chartId: 'people-snapshot-demographic-by-organization',
          title: {
            [Languages.EN]: 'Individual Count',
            [Languages.JA]: '従業員数',
          },
          layoutConfig: {
            gridColSpan: 1,
            gridRowSpan: 1,
          },
          componentConfig: {
            componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
            dataConfig: {
              queries: [
                {
                  metric: {
                    type: 'RegularMetricId',
                    value: RegularMetricId.Pm0001_1IndividualsCount,
                  },
                },
              ],
              timeSelection: {
                type: TimeSelectionType.FinancialYearYearly,
                input: { start: 2023, end: 2024 },
              },
            },
            selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
          },
        },
        {
          chartId: 'joiners-and-leavers-this-month',
          title: {
            [Languages.EN]: 'Joiners & Leavers',
            [Languages.JA]: '入社者と退職者',
          },
          layoutConfig: {
            gridColSpan: 2,
            gridRowSpan: 1,
          },
          componentConfig: {
            componentId: StoryboardComponentIds.OVERTIME_UNSEGMENTED,
            dataConfig: {
              queries: [
                {
                  metric: {
                    type: 'RegularMetricId',
                    value: RegularMetricId.Pm0006_1JoinersJoiners,
                  },
                },
                {
                  metric: {
                    type: 'RegularMetricId',
                    value: RegularMetricId.Pm0007_1LeaversTermDateLeavers,
                  },
                },
              ],
              timeSelection: {
                type: TimeSelectionType.CalendarYearMonthly,
                input: {
                  start: twelveMonthsAgoFromNowEndOfMonth,
                  end: nowEndOfMonth,
                },
              },
            },
            selectedView: OverTimeUnsegmentedVizViews.LINE_CHART,
          },
        },
      ],
    },
  ],
};
